/// <amd-module name="VSSUI/Components/ZeroData/ZeroData" />

import * as React from "react";
import { Button, IButtonProps } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { css } from "VSSUI/Util";
import { IZeroDataItem, IZeroDataMultipleProps, IZeroDataProps, ZeroDataActionType } from "./ZeroData.Props";

/**
 * Component for displaying helpful information when there is no data to show.
 */
export const ZeroData: React.SFC<IZeroDataProps> = props => {
    return <ZeroDataMultiple items={[props]} className={props.className} />;
};

/**
 * Component for displaying helpful information when there is no data to show. This one displays
 * multiple (or one) ZeroDataItems.
 *
 * THIS CLASS IS NOT EXPORTED presently because no design for multiple ZeroDataItems has yet been
 * approved.
 */
const ZeroDataMultiple: React.SFC<IZeroDataMultipleProps> = props => {
    const multiple = props.items.length > 1;
    return (
        <div className={css("vss-ZeroData flex-row justify-center", multiple ? "multiple" : "single", props.className)}>
            {props.items.map((item, index) => (
                <ZeroDataItem item={item} key={index} multiple={multiple} />
            ))}
        </div>
    );
};

interface IZeroDataItemProps {
    item: IZeroDataItem;
    /** true if there are multiple ZeroDataItems on screen */
    multiple: boolean;
}

/**
 * Represents a single item for the ZeroData component.
 */
class ZeroDataItem extends React.Component<IZeroDataItemProps> {
    public render(): JSX.Element {
        const item = this.props.item;
        let secondary: JSX.Element;
        if (typeof item.secondaryText === "string") {
            secondary = <span>{item.secondaryText}</span>;
        } else {
            secondary = item.secondaryText;
        }

        return (
            <div className={css("vss-ZeroDataItem flex-column flex-center", this.props.multiple && "flex-grow")}>
                {item.iconProps ? (
                    <Icon className={"vss-ZeroDataItem--icon-image"} {...item.iconProps} />
                ) : (
                    <img className="vss-ZeroDataItem--image" src={item.imagePath} alt={item.imageAltText} />
                )}

                <div className={css("vss-ZeroDataItem--primary margin-horizontal-16", this.props.multiple ? "title-m" : "title-l")}>
                    {item.primaryText}
                </div>
                {secondary && <div className="vss-ZeroDataItem--secondary margin-horizontal-16">{secondary}</div>}
                {this.renderAction(item)}
            </div>
        );
    }

    private renderAction(item: IZeroDataItem): React.ReactNode {
        if (item.renderAction) {
            return item.renderAction();
        }

        if (!item.actionText) {
            return null;
        }

        // actionType === 1 is for back-compat where it used to be button type
        // button type is rendered same as ctaButton now
        // we can remove actionType === 1 in 143
        if (item.actionType === ZeroDataActionType.ctaButton || (item.actionType as number) === 1) {
            const onActionClick = (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                if (item.onActionClick) {
                    item.onActionClick.call(null, ev, item);
                }
            };

            const buttonProps: IButtonProps = {
                ...(item.actionButtonProps || {}),
                className: "vss-ZeroDataItem--action",
                text: item.actionText,
                onClick: onActionClick,
                href: item.actionHref,
                role: item.actionHref ? "link" : undefined
            };

            return <Button {...buttonProps} primary={true} />;
        } else {
            return <Link href={item.actionHref}>{item.actionText}</Link>;
        }
    }
}
